import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import useWindowSize from '@rooks/use-window-size';
import { Link, graphql, useStaticQuery } from 'gatsby';
import { GatsbyImage } from 'gatsby-plugin-image';
import Moment from 'moment';

import { useMixpanel } from '../../functions/Mixpanel';

import * as style from './RecentArticles.module.css';

const sortByDate = (unsorted) => {
  unsorted.sort(function (a, b) {
    const dateA = new Date(a.node.createdAt);
    const dateB = new Date(b.node.createdAt);
    return new Date(dateB) - new Date(dateA);
  });
  return unsorted;
};

const sortByMostPopular = (unsorted) => {
  unsorted.sort(function (a, b) {
    if (!a.node.popularityPosition || a.node.popularityPosition > 6)
      a.node.popularityPosition = 999;
    if (!b.node.popularityPosition || b.node.popularityPosition > 6)
      b.node.popularityPosition = 999;
    return a.node.popularityPosition - b.node.popularityPosition;
  });
  return unsorted;
};

const RecentArticle = (props) => {
  const { title, img, link, date, clicked } = props;
  const { innerWidth } = useWindowSize();

  return (
    <div className={style.RecentArticle}>
      <Link to={`/blog/${link}/`} onClick={clicked}>
        <div className={innerWidth < 1023 ? style.mainContainerFlex : ''}>
          <a className={style.textContainer} href={`/${link}`}>
            <div className={style.titleContainer}>
              <p className="p2">{title}</p>
            </div>
            <div className={style.dateContainer}>
              <p className="caption">{date}</p>
            </div>
          </a>
          {innerWidth < 1023 && (
            <GatsbyImage
              image={img}
              alt={title}
              quality="100"
              webpoptions="true"
              className={style.blogImg}
            />
          )}
        </div>
      </Link>
    </div>
  );
};

const RecentArticles = ({ title, flagType }) => {
  const data = useStaticQuery(graphql`
    {
      allContentfulBlog {
        edges {
          node {
            slug
            title
            image {
              gatsbyImageData
            }
            createdAt(formatString: "D MMMM YYYY")
            popularityPosition
          }
        }
      }
    }
  `);

  const { innerWidth } = useWindowSize();

  const [noOfArticles, setNoOfArticles] = useState(3);
  const renderCounter = useRef(0);
  renderCounter.current = renderCounter.current + 1;

  useEffect(() => {
    function changeNoOfArticles() {
      console.log('change num of articles');
      if (innerWidth <= 767) {
        setNoOfArticles(1);
      } else if (innerWidth <= 1023) {
        setNoOfArticles(2);
      } else {
        setNoOfArticles(3);
      }
    }

    window.addEventListener('resize', changeNoOfArticles, false);
    changeNoOfArticles();
  }, [innerWidth]);

  const articles = useMemo(
    () => sortByDate(data.allContentfulBlog.edges),
    [data.allContentfulBlog.edges],
  );

  const topThreeArticles = useMemo(
    () =>
      articles
        .filter((item) => item.node.title !== title)
        .slice(0, noOfArticles),
    [articles, noOfArticles, title],
  );

  const mostPopularArticles = useMemo(
    () =>
      sortByMostPopular(articles)
        .filter((item) => item.node.popularityPosition !== null)
        .filter((item) => item.node.title !== title)
        .slice(0, noOfArticles),
    [articles, noOfArticles, title],
  );

  const [listOfArticles, setListOfArticles] = useState([]);

  useEffect(() => {
    if (flagType === 'Recent') {
      setListOfArticles(topThreeArticles);
    }
    if (flagType === 'Popular') {
      setListOfArticles(mostPopularArticles);
    }
  }, [flagType, mostPopularArticles, topThreeArticles]);

  const { mixpanel } = useMixpanel();

  const trackArticle = useCallback(
    (position, text) => {
      mixpanel.track(`View ${flagType} Article`, {
        'Article Title': text,
        'Order Number': position,
      });
    },
    [flagType, mixpanel],
  );

  return (
    <div className={style.ArticlesContainer}>
      {listOfArticles.slice(0, noOfArticles).map((article, index) => {
        return (
          <RecentArticle
            key={index}
            title={article.node.title}
            img={article.node?.image?.gatsbyImageData}
            link={article.node.slug}
            date={Moment(article.node.createdAt).format('MMMM, YYYY')}
            clicked={() => trackArticle(index + 1, article.node.title)}
          />
        );
      })}
    </div>
  );
};

export default RecentArticles;
